import { domainCopy } from '../copy';
import type { Language } from '../types/language';
import type { AdminScript, QuestMissionContent } from '../types';

/**
 * **The words a child meets in one mission, each with where it sits and the role that decides its limits**
 * (CONTENT_ENGINE.md §3.2). One walk, shared by the version diff (O13) and the checker (§5), so both see the same
 * fields in the same order. `path` addresses the field in `QuestMissionContent`; `where` is what O13 and the checker
 * print ("Beat 2 · reader", "Question 1 · tile 3").
 */
export type QuestTextRole =
  | 'beat'
  | 'spoken'
  | 'choice'
  | 'consequence'
  | 'question'
  | 'tileCaption'
  | 'feedback';

export interface QuestContentField {
  path: Array<string | number>;
  role: QuestTextRole;
  where: string;
  text: string;
}

const text = (s: AdminScript | string | undefined): string =>
  typeof s === 'string' ? s : (s?.text ?? '');

export function questContentFields(m: QuestMissionContent, language?: Language): QuestContentField[] {
  const w = domainCopy(language).questWhere;
  const out: QuestContentField[] = [];
  (m.beats ?? []).forEach((b, i) => {
    const beat = i + 1;
    out.push({ path: ['beats', i, 'prompt'], role: 'beat', where: w.beatReader(beat), text: text(b.prompt) });
    out.push({ path: ['beats', i, 'spoken'], role: 'spoken', where: w.beatPrereader(beat), text: text(b.spoken) });
    (b.choices ?? []).forEach((c, j) => {
      out.push({
        path: ['beats', i, 'choices', j, 'label'],
        role: 'choice',
        where: w.beatChoice(beat, c.key),
        text: text(c.label),
      });
      out.push({
        path: ['beats', i, 'choices', j, 'consequence'],
        role: 'consequence',
        where: w.beatChoiceConsequence(beat, c.key),
        text: text(c.consequence),
      });
    });
  });
  m.quiz.forEach((q, i) => {
    const question = i + 1;
    out.push({ path: ['quiz', i, 'prompt'], role: 'question', where: w.question(question), text: q.prompt });
    out.push({ path: ['quiz', i, 'spoken'], role: 'spoken', where: w.questionPrereader(question), text: text(q.spoken) });
    q.tiles.forEach((t, j) =>
      out.push({
        path: ['quiz', i, 'tiles', j, 'caption'],
        role: 'tileCaption',
        where: w.questionTile(question, j + 1),
        text: t.caption,
      }),
    );
    out.push({
      path: ['quiz', i, 'feedback', 'correct'],
      role: 'feedback',
      where: w.questionFeedbackRight(question),
      text: q.feedback?.correct ?? '',
    });
    out.push({
      path: ['quiz', i, 'feedback', 'explain'],
      role: 'feedback',
      where: w.questionFeedback(question),
      text: q.feedback?.explain ?? '',
    });
  });
  return out;
}

export type DiffTokenKind = 'same' | 'del' | 'add';
export interface DiffToken {
  text: string;
  kind: DiffTokenKind;
}
/** One changed field: its label, and its words before and after — deleted words in `before`, added in `after`. */
export interface QuestDiffRow {
  where: string;
  before: DiffToken[];
  after: DiffToken[];
}

/**
 * Word-level diff inside one field (the prototype's `wordDiff`, docs/handoff/design/content-engine/app.js): a longest
 * common subsequence over words, keeping the whitespace between them as tokens so the text reads back exactly.
 */
export function wordDiff(a: string, b: string): { before: DiffToken[]; after: DiffToken[] } {
  const A = a.split(/(\s+)/).filter((t) => t !== '');
  const B = b.split(/(\s+)/).filter((t) => t !== '');
  const n = A.length;
  const m = B.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i]![j] = A[i] === B[j] ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
  const before: DiffToken[] = [];
  const after: DiffToken[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) {
      before.push({ text: A[i]!, kind: 'same' });
      after.push({ text: B[j]!, kind: 'same' });
      i++;
      j++;
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) before.push({ text: A[i++]!, kind: 'del' });
    else after.push({ text: B[j++]!, kind: 'add' });
  }
  while (i < n) before.push({ text: A[i++]!, kind: 'del' });
  while (j < m) after.push({ text: B[j++]!, kind: 'add' });
  return { before, after };
}

/** How a tile's picture reads in a diff: the key it names, type by choice, or whatever its id resolves to. */
function tileArtLabel(t: { art?: string | null }, language?: Language): string {
  const d = domainCopy(language).questDiff;
  if (t.art === null) return d.typeChosen;
  return t.art ? t.art : d.fromTileId;
}

/**
 * **O13's diff between any two versions, or a version and the draft** (CONTENT_ENGINE.md §8: "a diff between any
 * two"). Field by field, only where the text differs, word by word inside each field: title, level name, mechanic,
 * where it points, every word a child reads, each tile's picture, tiles added or removed, and each answer.
 */
export function diffQuestContent(
  a: QuestMissionContent,
  b: QuestMissionContent,
  language?: Language,
): QuestDiffRow[] {
  const d = domainCopy(language).questDiff;
  const w = domainCopy(language).questWhere;
  const art = (t: { art?: string | null }) => tileArtLabel(t, language);
  const rows: QuestDiffRow[] = [];
  const push = (where: string, x: string | undefined, y: string | undefined) => {
    if ((x ?? '') !== (y ?? '')) rows.push({ where, ...wordDiff(x ?? '', y ?? '') });
  };
  push(d.title, a.title, b.title);
  push(d.levelName, a.levelName, b.levelName);
  push(d.mechanic, a.mechanic, b.mechanic);
  push(d.pointsAt, a.deepLink ?? d.none, b.deepLink ?? d.none);
  push(d.badge, a.badge?.name, b.badge?.name);
  push(d.picture, a.art, b.art);
  const fa = questContentFields(a, language);
  const fb = questContentFields(b, language);
  const key = (f: QuestContentField) => f.path.join('.');
  const keys = [...new Set([...fa, ...fb].map(key))];
  for (const k of keys) {
    const x = fa.find((f) => key(f) === k);
    const y = fb.find((f) => key(f) === k);
    // a tile on one side only is one row below ("Question 1 · tile 3"), with its caption and picture — not two
    if ((!x || !y) && (x ?? y)!.path[2] === 'tiles') continue;
    push((y ?? x)!.where, x?.text, y?.text);
  }
  b.quiz.forEach((q, qi) =>
    q.tiles.forEach((t, ti) => {
      const o = a.quiz[qi]?.tiles[ti];
      if (!o) push(w.questionTile(qi + 1, ti + 1), '', `${t.caption} (${art(t)})`);
      else if (art(o) !== art(t)) push(w.questionTilePicture(qi + 1, ti + 1), art(o), art(t));
    }),
  );
  a.quiz.forEach((q, qi) =>
    q.tiles.forEach((t, ti) => {
      if (!b.quiz[qi]?.tiles[ti])
        push(w.questionTile(qi + 1, ti + 1), `${t.caption} (${art(t)})`, '');
    }),
  );
  b.quiz.forEach((q, qi) => {
    const o = a.quiz[qi];
    if (o && o.answerTileId !== q.answerTileId) push(w.questionAnswer(qi + 1), o.answerTileId, q.answerTileId);
  });
  return rows;
}
